/**
 * arSessionPayload — turn an editor project into the Firestore `arSessions` doc
 * payload that the publish-on-Run hook writes. Pure: no Firebase, no DOM.
 *
 * The writer is responsible for adding `updatedAt: serverTimestamp()`.
 */

import { convertSketch } from './p5ToInstance';
import { resolvePathToFile } from '../modules/Preview/filePath';
import { MEDIA_FILE_QUOTED_REGEX, STRING_REGEX } from './fileUtils';
import { currentP5Version } from '../../common/p5Versions';

// The core p5 library CDN URLs the editor emits (cdnjs / jsdelivr, min or not).
// Kept in sync with the version-detection regexes in
// `client/modules/IDE/hooks/useP5Version.jsx`. Addon URLs (p5.sound, lil-gui, the
// compat addons) deliberately DON'T match — only the core lib carries the version
// the AR viewer must load.
const P5_CORE_URL_REGEXES = [
  /^https?:\/\/cdnjs\.cloudflare\.com\/ajax\/libs\/p5\.js\/(.+)\/p5\.(?:min\.)?js$/,
  /^https?:\/\/cdn\.jsdelivr\.net\/npm\/p5@(.+)\/lib\/p5\.(?:min\.)?js$/
];

/**
 * Pick the file to run in AR: the p5 editor's main file (`sketch.js`) by
 * convention, otherwise the first JavaScript file. Folders / non-js are ignored.
 *
 * @param {Array<{name: string, fileType: string, content: string}>} files
 * @returns {object|null}
 */
function findMainSketchFile(files) {
  if (!Array.isArray(files)) return null;
  const jsFiles = files.filter(
    (f) =>
      f &&
      f.fileType === 'file' &&
      typeof f.name === 'string' &&
      f.name.endsWith('.js')
  );
  return jsFiles.find((f) => f.name === 'sketch.js') || jsFiles[0] || null;
}

/** The basename of a script src ("assets/orion.js" → "orion.js"). */
function basename(src) {
  return src.split('/').pop();
}

/** A script src that points off-origin (p5 CDN etc.) — never a project file. */
function isExternal(src) {
  return /^(https?:)?\/\//i.test(src) || src.includes('://');
}

/**
 * Collect the sketch's non-main JS files referenced by `index.html`'s <script>
 * tags, in tag order, as `{ name, code }`. These are handed to the AR viewer so it
 * can rebuild globals (e.g. `window.constellations`) before running the sketch —
 * mirroring how the editor preview loads the same tags before `sketch.js`.
 *
 * External srcs (p5 CDN) and the main file itself are excluded. Emitted raw (not
 * AST-converted) — correct for pure data files.
 *
 * @param {Array<{name:string, fileType:string, content:string}>} files
 * @param {string} mainName  the main sketch filename to exclude (e.g. "sketch.js")
 * @returns {Array<{name:string, code:string}>}
 */
function collectHelperScripts(files, mainName) {
  if (!Array.isArray(files)) return [];
  const html = files.find(
    (f) => f && f.fileType === 'file' && f.name === 'index.html'
  );
  if (!html || typeof html.content !== 'string') return [];

  const scripts = [];
  const seen = new Set();
  const tagRe = /<script\s+[^>]*src=["']([^"']+)["'][^>]*>/gi;
  let match = tagRe.exec(html.content);
  while (match) {
    const src = match[1];
    const name = basename(src);
    if (!isExternal(src) && name !== mainName && !seen.has(name)) {
      const referenced = files.find(
        (f) => f && f.fileType === 'file' && f.name === name
      );
      if (referenced) {
        seen.add(name);
        scripts.push({ name, code: referenced.content || '' });
      }
    }
    match = tagRe.exec(html.content);
  }
  return scripts;
}

/**
 * Read the p5.js version the sketch's `index.html` pins on its core-library <script>
 * tag, so the AR viewer can load the SAME p5 build (a mismatch throws
 * "p.<fn> is not a function" for API added in a newer p5 — the converter emits
 * `p.<fn>()` for whatever version the sketch was written against).
 *
 * Regex-based (not DOMParser) to stay pure/Node-safe like the rest of this module;
 * mirrors the detection in `useP5Version.jsx`. Falls back to `currentP5Version` when
 * no core p5 tag is found (old/hand-edited HTML) so the viewer always gets a version.
 *
 * @param {Array<{name:string, fileType:string, content:string}>} files
 * @returns {string} a p5 version string (e.g. "1.11.13")
 */
function extractP5Version(files) {
  if (!Array.isArray(files)) return currentP5Version;
  const html = files.find(
    (f) => f && f.fileType === 'file' && f.name === 'index.html'
  );
  if (!html || typeof html.content !== 'string') return currentP5Version;

  const tagRe = /<script\s+[^>]*src=["']([^"']+)["'][^>]*>/gi;
  let match = tagRe.exec(html.content);
  while (match) {
    const src = match[1];
    const hit = P5_CORE_URL_REGEXES.map((re) => re.exec(src)).find((m) => m);
    if (hit) return hit[1];
    match = tagRe.exec(html.content);
  }
  return currentP5Version;
}

/**
 * Rewrite relative media references (images/fonts/audio) in a chunk of sketch source
 * to their Firebase Storage download URLs, so the AR viewer — a different origin with
 * no access to the project's files — can fetch them. Uploaded media live in Storage;
 * the `ProjectFile` only carries a `url` (empty `content`). Typed-in code/data files
 * have `content` but no `url`, so the `resolved.url` guard naturally scopes this to
 * genuine Storage assets and leaves everything else alone.
 *
 * This mirrors the editor preview's own rewrite (`resolveJSLinksInString` in
 * `client/modules/Preview/EmbedFrame.jsx`) — same `resolvePathToFile` + media regex —
 * minus the blob-URL branch (blob URLs are same-document only and can't cross to the
 * phone). Purely string-level: a literal is replaced only when it matches the media
 * regex AND resolves to a file with a `url`.
 *
 * @param {string} source  a chunk of JS source (the converted sketch or a helper script)
 * @param {Array<{name:string, url?:string, children?:string[]}>} files  the editor's
 *   `state.files` tree (must include the `root` folder node `resolvePathToFile` walks)
 * @returns {string} the source with resolvable media literals swapped for Storage URLs
 */
function rewriteMediaUrls(source, files) {
  if (typeof source !== 'string' || source === '') return source;
  // `resolvePathToFile` walks from a `root` node; without one it would throw. A files
  // array lacking root (or not an array) simply can't resolve anything — pass through.
  if (!Array.isArray(files) || !files.some((f) => f && f.name === 'root')) {
    return source;
  }

  const literals = source.match(STRING_REGEX) || [];
  const seen = new Set();
  let out = source;
  literals.forEach((literal) => {
    if (seen.has(literal) || !literal.match(MEDIA_FILE_QUOTED_REGEX)) return;
    seen.add(literal);
    const quote = literal[0];
    const path = literal.slice(1, -1);
    const resolved = resolvePathToFile(path, files);
    if (resolved && resolved.url) {
      // split/join replaces every occurrence of this exact quoted literal in one pass.
      out = out.split(literal).join(quote + resolved.url + quote);
    }
  });
  return out;
}

/**
 * @param {object} args
 * @param {Array} args.files       the project's ProjectFile[]
 * @param {string} args.ownerId    the authoring user's auth uid
 * @param {number} [args.prevRunId] the current runId on the session doc (0 if new)
 * @param {boolean} [args.stripOrbitControl] drop orbitControl(...) from the converted
 *   code (it fights the AR bridge camera); resolved from arSettings by the publisher.
 * @param {boolean} [args.stripBackground] drop background(...) from the converted code
 *   (it paints over the AR camera feed); resolved from arSettings by the publisher.
 * @returns {{ code: string, runId: number, width: number|null, height: number|null, webgl: boolean, p5Version: string, ownerId: string, usesDegrees: boolean }}
 *   `usesDegrees` is an EDITOR-ONLY signal (it drives the "use radians" warning bar) and is
 *   deliberately NOT part of the Firestore document — publishArSession destructures the
 *   fields it writes, so this one never reaches the session doc. Keep it that way.
 */
function buildArSessionPayload({
  files,
  ownerId,
  prevRunId,
  stripOrbitControl,
  stripBackground
}) {
  const main = findMainSketchFile(files);
  if (!main) {
    throw new Error(
      'Cannot publish to AR: no JavaScript sketch file was found to convert.'
    );
  }

  const { factorySource, metadata } = convertSketch(main.content || '', {
    stripOrbitControl,
    stripBackground
  });

  return {
    // Swap relative media paths for their Storage URLs so the viewer (a different
    // origin) can fetch images/fonts/audio the same way the editor preview does.
    code: rewriteMediaUrls(factorySource, files),
    runId: (prevRunId || 0) + 1,
    width: metadata.width,
    height: metadata.height,
    webgl: metadata.webgl,
    // The p5 build the viewer must load so its `p.<fn>()` calls resolve — parsed from
    // the same index.html the editor preview uses (keeps editor + AR in lockstep).
    p5Version: extractP5Version(files),
    ownerId,
    helperScripts: collectHelperScripts(files, main.name).map((helper) => ({
      name: helper.name,
      code: rewriteMediaUrls(helper.code, files)
    })),
    // Editor-only; see the @returns note. Never written to Firestore.
    usesDegrees: metadata.usesDegrees
  };
}

export {
  buildArSessionPayload,
  findMainSketchFile,
  collectHelperScripts,
  extractP5Version,
  rewriteMediaUrls
};
